import React from 'react';
import clsx from 'clsx';
import { Icon, IconName } from './Icon';

/**
 * Shared building blocks for the orange-led v2 teacher-dashboard design system.
 *
 * Every surface here is pinned to the light theme (no `dark:` variants) so the
 * manage area matches the design-system mockups regardless of the OS colour
 * scheme. Compose pages from these rather than re-deriving the styles.
 */

/* -------------------------------------------------------------------------- */
/* Card                                                                       */
/* -------------------------------------------------------------------------- */

export const DsCard = ({
  children,
  className,
}: React.PropsWithChildren<{ className?: string }>) => (
  <div
    className={clsx(
      'rounded-2xl border border-pt-ds-line bg-white p-5',
      className,
    )}
  >
    {children}
  </div>
);

/* -------------------------------------------------------------------------- */
/* Button                                                                     */
/* -------------------------------------------------------------------------- */

export type DsButtonVariant = 'green' | 'orange' | 'out' | 'soft';

interface DsButtonProps {
  variant?: DsButtonVariant;
  href?: string;
  icon?: IconName;
  iconRight?: IconName;
  className?: string;
  onClick?: () => void;
}

const dsButtonClasses = (variant: DsButtonVariant) =>
  clsx(
    'inline-flex items-center justify-center gap-2 rounded-[10px] px-4 py-3 font-inter text-sm font-semibold no-underline',
    variant === 'green' && 'bg-pt-bottle-green text-white hover:opacity-90',
    variant === 'orange' && 'bg-pt-orange text-white hover:bg-pt-orange-dark',
    variant === 'out' &&
      'border-[1.5px] border-pt-ds-line bg-white text-pt-ds-ink hover:bg-pt-cream',
    variant === 'soft' && 'bg-pt-ds-soft-green text-pt-bottle-green',
  );

export const DsButton = ({
  variant = 'green',
  href,
  icon,
  iconRight,
  className,
  onClick,
  children,
}: React.PropsWithChildren<DsButtonProps>) => {
  const content = (
    <>
      {icon && <Icon name={icon} size={18} />}
      {children}
      {iconRight && <Icon name={iconRight} size={16} />}
    </>
  );

  return href ? (
    <a href={href} className={clsx(dsButtonClasses(variant), className)}>
      {content}
    </a>
  ) : (
    <button
      type="button"
      onClick={onClick}
      className={clsx(dsButtonClasses(variant), className)}
    >
      {content}
    </button>
  );
};

/* -------------------------------------------------------------------------- */
/* Page header                                                                */
/* -------------------------------------------------------------------------- */

interface PageHeadProps {
  title: string;
  subtitle?: string;
  crumb?: string;
  actions?: React.ReactNode;
}

export const PageHead = ({
  title,
  subtitle,
  crumb,
  actions,
}: PageHeadProps) => (
  <div>
    {crumb && (
      <p className="mb-2 font-inter text-[13px] text-pt-ds-faint">{crumb}</p>
    )}
    <div className="flex flex-wrap items-end gap-4">
      <div>
        <h1 className="font-display text-[30px] font-bold leading-tight text-pt-ds-ink">
          {title}
        </h1>
        {subtitle && (
          <p className="mt-0.5 font-inter text-sm text-pt-ds-muted">
            {subtitle}
          </p>
        )}
      </div>
      {actions && (
        <div className="ml-auto flex flex-wrap gap-2.5">{actions}</div>
      )}
    </div>
  </div>
);

/* -------------------------------------------------------------------------- */
/* Score ring (conic-gradient progress dial)                                  */
/* -------------------------------------------------------------------------- */

export const ScoreRing = ({ value }: { value: number }) => {
  const pct = Math.max(0, Math.min(100, value));

  return (
    <span
      className="grid h-[38px] w-[38px] place-items-center rounded-full"
      style={{
        background: `conic-gradient(var(--color-pt-ds-purple) 0 ${pct}%, var(--color-pt-ds-purple-soft) ${pct}% 100%)`,
      }}
    >
      <i className="h-[26px] w-[26px] rounded-full bg-white" />
    </span>
  );
};

/* -------------------------------------------------------------------------- */
/* KPI card                                                                   */
/* -------------------------------------------------------------------------- */

export type KpiTint = 'purple' | 'blue' | 'green' | 'orange' | 'sun' | 'red';

const kpiTintClasses: Record<KpiTint, string> = {
  purple: 'bg-pt-ds-purple-soft text-pt-ds-purple',
  blue: 'bg-pt-ds-blue-soft text-pt-ds-blue',
  green: 'bg-pt-sinbad text-pt-primary',
  orange: 'bg-pt-orange-soft text-pt-orange',
  sun: 'bg-pt-ds-yellow-soft text-[#b8860b]',
  red: 'bg-pt-ds-berry-soft text-pt-coral-red',
};

interface KpiCardProps {
  tint: KpiTint;
  icon?: IconName;
  glyph?: string;
  value: string;
  label: string;
}

export const KpiCard = ({ tint, icon, glyph, value, label }: KpiCardProps) => (
  <div className="flex items-center gap-3 rounded-2xl border border-pt-ds-line bg-white p-4">
    <span
      className={clsx(
        'grid h-12 w-12 shrink-0 place-items-center rounded-xl',
        kpiTintClasses[tint],
      )}
    >
      {glyph ? (
        <span className="font-display text-2xl font-extrabold leading-none">
          {glyph}
        </span>
      ) : (
        icon && <Icon name={icon} size={26} />
      )}
    </span>

    <span className="flex flex-col">
      <span className="font-display text-2xl font-bold leading-none text-pt-ds-ink">
        {value}
      </span>
      <span className="mt-1 font-inter text-[13px] text-pt-ds-muted">
        {label}
      </span>
    </span>
  </div>
);

/* -------------------------------------------------------------------------- */
/* Badge                                                                      */
/* -------------------------------------------------------------------------- */

export type BadgeTone = 'green' | 'orange' | 'blue' | 'purple' | 'grey' | 'red';

const badgeToneClasses: Record<BadgeTone, string> = {
  green: 'bg-pt-sinbad text-pt-bottle-green',
  orange: 'bg-pt-orange-soft text-pt-orange',
  blue: 'bg-pt-ds-blue-soft text-pt-ds-blue',
  purple: 'bg-pt-ds-purple-soft text-pt-ds-purple',
  grey: 'bg-pt-ds-line text-pt-ds-muted',
  red: 'bg-pt-ds-berry-soft text-pt-coral-red',
};

export const Badge = ({
  tone,
  children,
}: React.PropsWithChildren<{ tone: BadgeTone }>) => (
  <span
    className={clsx(
      'inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 font-inter text-xs font-bold',
      badgeToneClasses[tone],
    )}
  >
    {children}
  </span>
);

/* -------------------------------------------------------------------------- */
/* Pill (toolbar filter/sort)                                                 */
/* -------------------------------------------------------------------------- */

interface PillProps {
  icon?: IconName;
  iconRight?: IconName;
  selected?: boolean;
  onClick?: () => void;
  className?: string;
}

export const Pill = ({
  icon,
  iconRight,
  selected = false,
  onClick,
  className,
  children,
}: React.PropsWithChildren<PillProps>) => (
  <button
    type="button"
    onClick={onClick}
    aria-pressed={selected}
    className={clsx(
      'inline-flex items-center gap-2 rounded-[10px] border px-3.5 py-2 font-inter text-[13px] font-semibold',
      selected
        ? 'border-pt-orange bg-pt-orange-soft text-pt-orange'
        : 'border-pt-ds-line bg-white text-pt-ds-ink',
      className,
    )}
  >
    {icon && (
      <Icon
        name={icon}
        size={16}
        className={selected ? undefined : 'text-pt-ds-muted'}
      />
    )}
    {children}
    {iconRight && (
      <Icon
        name={iconRight}
        size={14}
        className={selected ? undefined : 'text-pt-ds-muted'}
      />
    )}
  </button>
);

/* -------------------------------------------------------------------------- */
/* Progress bar                                                               */
/* -------------------------------------------------------------------------- */

export const DsProgressBar = ({ value }: { value: number }) => (
  <div className="h-2 overflow-hidden rounded-full bg-pt-ds-line">
    <span
      className="block h-full rounded-full bg-pt-primary"
      style={{ width: `${Math.max(0, Math.min(100, value))}%` }}
    />
  </div>
);
